import { buildApiUrl, tenantApiPrefix } from '../config';
import { getTenantIdFromStorage } from '../utils/onboardingLocalStorage';
import {
  L2C_MOVERS_PAGE_SIZE,
  type L2CChainSnapshot,
  type L2CComparisonOption,
  type L2CComparisonPreset,
  type L2CMetric,
  type L2CMetricBreakdown,
  type L2CPeriodGrain,
  type L2CStageDetail,
  type L2CStageDetailRequest,
  type L2CTrendPoint,
} from '../types/leadToCash';

export interface L2CChainQuery {
  grain?: L2CPeriodGrain;
  preset?: L2CComparisonPreset;
  currentStart?: string;
  currentEnd?: string;
  compareStart?: string;
  compareEnd?: string;
  refresh?: boolean;
}

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('klaris_jwt');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

function requireTenantPrefix(): string {
  const tid = getTenantIdFromStorage();
  if (!tid) throw new Error('Not authenticated');
  return tenantApiPrefix(tid);
}

async function readJson<T>(response: Response, fallbackMessage: string): Promise<T> {
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const detail = body && typeof body === 'object' ? (body as { detail?: string }).detail : null;
    throw new Error(typeof detail === 'string' ? detail : fallbackMessage);
  }
  return response.json();
}

function chainQuery({
  grain = 'week',
  preset = 'custom',
  currentStart,
  currentEnd,
  compareStart,
  compareEnd,
  refresh,
}: L2CChainQuery): string {
  const params = new URLSearchParams({ grain, preset });
  if (currentStart && currentEnd) {
    params.set('current_start', currentStart);
    params.set('current_end', currentEnd);
    if (compareStart && compareEnd) {
      params.set('compare_start', compareStart);
      params.set('compare_end', compareEnd);
    }
  }
  if (refresh) params.set('refresh', 'true');
  return params.toString();
}

export async function fetchLeadToCashComparisonOptions(
  grain: L2CPeriodGrain = 'week',
  signal?: AbortSignal,
): Promise<{ grain: L2CPeriodGrain; options: L2CComparisonOption[] }> {
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/lead-to-cash/comparison-options?grain=${grain}`),
    {
      headers: getAuthHeaders(),
      signal: signal ?? AbortSignal.timeout(60_000),
    },
  );
  return readJson(response, 'Failed to load comparison options');
}

export async function fetchLeadToCashChain(
  query: L2CChainQuery = {},
): Promise<L2CChainSnapshot> {
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/lead-to-cash/chain?${chainQuery(query)}`),
    { headers: getAuthHeaders() },
  );
  return readJson<L2CChainSnapshot>(response, 'Failed to load Lead-to-Cash chain');
}

export interface L2CStageDetailQuery {
  driverId?: string;
  moversPage?: number;
  moversPageSize?: number;
}

export async function fetchLeadToCashStageDetail(
  stageId: string,
  payload: L2CStageDetailRequest,
  query: L2CStageDetailQuery = {},
): Promise<L2CStageDetail> {
  const params = new URLSearchParams();
  if (query.driverId) params.set('driver_id', query.driverId);
  if (query.moversPage) params.set('movers_page', String(query.moversPage));
  params.set('movers_page_size', String(query.moversPageSize ?? L2C_MOVERS_PAGE_SIZE));
  const qs = params.toString();
  const response = await fetch(
    buildApiUrl(
      `${requireTenantPrefix()}/lead-to-cash/chain/stages/${encodeURIComponent(stageId)}/detail${qs ? `?${qs}` : ''}`,
    ),
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    },
  );
  return readJson<L2CStageDetail>(response, 'Failed to load stage detail');
}

export async function rerunLeadToCashChain(
  query: L2CChainQuery = {},
): Promise<L2CChainSnapshot> {
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/lead-to-cash/rerun?${chainQuery(query)}`),
    { method: 'POST', headers: getAuthHeaders() },
  );
  return readJson<L2CChainSnapshot>(response, 'Failed to refresh Lead-to-Cash chain');
}

export interface L2CMetricBreakdownRequest {
  grain: L2CPeriodGrain;
  preset: L2CComparisonPreset;
  current_period: { start?: string | null; end?: string | null };
  prior_period: { start?: string | null; end?: string | null };
  comparison_mode?: string | null;
  comparison_label?: string | null;
  /** The clicked metric card's current snapshot — used to build its plain-language summary. */
  metric?: L2CMetric | null;
}

export async function fetchLeadToCashMetricBreakdown(
  stageId: string,
  metricKey: string,
  payload: L2CMetricBreakdownRequest,
  driverId?: string,
): Promise<L2CMetricBreakdown> {
  const params = new URLSearchParams();
  if (driverId) params.set('driver_id', driverId);
  const qs = params.toString();
  const response = await fetch(
    buildApiUrl(
      `${requireTenantPrefix()}/lead-to-cash/chain/stages/${encodeURIComponent(stageId)}/metrics/${encodeURIComponent(metricKey)}/breakdown${qs ? `?${qs}` : ''}`,
    ),
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    },
  );
  return readJson(response, 'Failed to load metric breakdown');
}

export async function fetchLeadToCashMetricTrend(
  stageId: string,
  metricKey: string,
  limit = 12,
): Promise<{ stage_id: string; metric_key: string; points: L2CTrendPoint[] }> {
  const response = await fetch(
    buildApiUrl(
      `${requireTenantPrefix()}/lead-to-cash/chain/stages/${encodeURIComponent(stageId)}/metrics/${encodeURIComponent(metricKey)}/trend?limit=${limit}`,
    ),
    { headers: getAuthHeaders() },
  );
  return readJson(response, 'Failed to load metric trend');
}
